import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CAlert,
  CTable,
  CFormInput,
  CInputGroup,
  CBadge,
  CPagination,
  CPaginationItem,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CListGroup,
  CListGroupItem,
  CTooltip,
  CFormSelect,
  CForm,
  CFormLabel,
  CFormTextarea,
  CFormCheck
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
  cilMagnifyingGlass, 
  cilReload,
  cilArrowRight,
  cilArrowLeft,
  cilList,
  cilInfo,
  cilCalendar,
  cilDollar,
  cilUser,
  cilBuilding,
  cilPhone,
  cilSync,
  cilPencil,
  cilTrash,
  cilBell,
  cilBellExclamation
} from '@coreui/icons';
import { cilEnvelopeClosed } from '@coreui/icons';
import { superAdminAPI } from '../services/api';
import QuickBooksRenewalModal from '../components/QuickBooksRenewalModal';

const SuperAdminOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'end_date', direction: 'asc' });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [reminderStatusFilter, setReminderStatusFilter] = useState('all');
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Reminder control modal state
  const [showReminderControlModal, setShowReminderControlModal] = useState(false);
  const [selectedReminderOrder, setSelectedReminderOrder] = useState(null);
  const [reminderAction, setReminderAction] = useState('pause');
  const [reminderReason, setReminderReason] = useState('');
  const [reminderControlLoading, setReminderControlLoading] = useState(false);
  
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedRenewalOrder, setSelectedRenewalOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [reseller, setReseller] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const extractValue = (value) => {
    if (value == null || value === undefined) return '';
    
    if (typeof value === 'object' && value !== null) {
      if (value.$date) {
        return value.$date;
      }
      if (value.$numberDouble) {
        const num = parseFloat(value.$numberDouble);
        return isNaN(num) ? '' : num;
      }
      if (value.$numberInt) {
        return parseInt(value.$numberInt);
      }
      if (value.$numberLong) {
        return parseInt(value.$numberLong);
      }
      const extracted = Object.values(value)[0];
      return extracted !== undefined ? extracted : value;
    }
    
    return value;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const num = extractValue(amount);
    if (num === '' || isNaN(num)) return 'N/A';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // NEW: Function to format total amount with quantity
  const formatTotalAmount = (order) => {
    const unitPrice = extractValue(order.retail_subscription_price) || 0;
    const quantity = extractValue(order.qty) || extractValue(order.quantity) || 1;
    const totalAmount = unitPrice * quantity;
    
    return {
      unitPrice: formatCurrency(unitPrice, order.Currency),
      quantity: quantity,
      total: formatCurrency(totalAmount, order.Currency),
      totalRaw: totalAmount
    };
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      let dateString;
      const extractedValue = extractValue(dateValue);
      
      if (!extractedValue) return 'N/A';
      
      dateString = String(extractedValue);
      
      if (!dateString) return 'N/A';
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const date = new Date(parts[2], parts[0] - 1, parts[1]);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
          }
        }
        return dateString;
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const getStatusColor = (status) => {
    const statusStr = extractValue(status);
    if (!statusStr) return 'info';
    
    switch (statusStr.toLowerCase()) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'danger';
      case 'cancelled': return 'secondary';
      case 'pending_payment': return 'warning';
      default: return 'info';
    }
  };

  const getReminderStatus = (order) => {
    const status = extractValue(order.reminder_status);
    return status || 'active';
  };

  const getReminderStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      default: return 'info';
    }
  };

  const getReminderStatusText = (status) => {
    switch (status) {
      case 'active': return 'Reminders Active';
      case 'paused': return 'Reminders Paused';
      default: return 'Unknown';
    }
  };

  const safeRender = (value) => {
    const extracted = extractValue(value);
    if (extracted === '') return 'N/A';
    return String(extracted);
  };

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await superAdminAPI.getOrders();
      if (response.data.success) {
        const processedOrders = response.data.orders.map(order => ({
          ...order,
          retail_subscription_price: extractValue(order.retail_subscription_price),
          renewal_amount_customer: extractValue(order.renewal_amount_customer),
          distributor_reseller: extractValue(order.distributor_reseller),
          qty: extractValue(order.qty) || 1,
          quantity: extractValue(order.qty) || 1,
          reminder_status: extractValue(order.reminder_status) || 'active'
        }));
        setOrders(processedOrders);
      } else {
        setError('Failed to fetch orders');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await superAdminAPI.getSoftwareProducts();
      if (response.data.success) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchResellerInfo = async (resellerId) => {
    try {
      if (!resellerId) return null;
      
      try {
        const response = await superAdminAPI.getSingleReseller(resellerId);
        if (response.data.success && response.data.reseller) {
          return response.data.reseller;
        }
      } catch (error) {
        console.log("Could not find reseller by _id, trying other methods");
      }
      
      const allResellers = await superAdminAPI.getResellersForDropdown();
      if (allResellers.data.success) {
        const foundReseller = allResellers.data.resellers.find(
          r => r._id === resellerId || r.resellerid === resellerId
        );
        if (foundReseller) {
          return foundReseller;
        }
      }
      
      console.warn("Reseller not found with ID:", resellerId);
      return null;
      
    } catch (error) {
      console.error("Error fetching reseller info:", error);
      return null;
    }
  };

  const canHaveCartridges = (order) => {
    if (!order) return false;

    const subscriptionPlan = safeRender(order.subscription_plan).toLowerCase();

    if (subscriptionPlan.includes("unlimited")) {
      return false;
    }

    if (
      subscriptionPlan.includes("gb") ||
      subscriptionPlan.includes("tb") ||
      subscriptionPlan.includes("mb")
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    fetchOrders();
    fetchProducts();
  }, [refreshTrigger]);

  const extractDateForInput = (dateValue) => {
    const extracted = extractValue(dateValue);
    if (!extracted) return '';
    
    if (typeof extracted === 'string') {
      if (extracted.includes('/')) {
        const parts = extracted.split('/');
        if (parts.length === 3) {
          const [month, day, year] = parts;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      if (extracted.includes('T')) {
        return extracted.split('T')[0];
      }
      try {
        const date = new Date(extracted);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch (e) {
        console.log('Date parsing error:', e);
      }
    }
    
    return '';
  };

  const handleEditClick = (order) => {
    setEditingOrder(order);
    
    const formData = {
      account_name: extractValue(order.account_name) || '',
      reseller_margin: extractValue(order.reseller_margin) || 0,
      invoice_owner: extractValue(order.invoice_owner) || '',
      distributor_reseller: extractValue(order.distributor_reseller) || '',
      subscription_plan: extractValue(order.subscription_plan) || '',
      initial_amount_customer: extractValue(order.initial_amount_customer) || 
                            extractValue(order.retail_subscription_price) || 0,
      renewal_amount_customer: extractValue(order.renewal_amount_customer) || 
                             extractValue(order.retail_subscription_price) || 0,
      initial_amount_partner: extractValue(order.initial_amount_partner) || 0,
      renewal_amount_partner: extractValue(order.renewal_amount_partner) || 0,
      partner_discount_rate: extractValue(order.partner_discount_rate) || 0,
      partner_margin_percentage: extractValue(order.partner_margin_percentage) || 0,
      payment_terms: extractValue(order.payment_terms) || 'immediate',
      start_date: extractDateForInput(order.start_date),
      end_date: extractDateForInput(order.end_date),
      product_owner_name: extractValue(order.product_owner_name) || '',
      product_owner_email: extractValue(order.product_owner_email) || 
                         extractValue(order.product_owner_email_2) || '',
      partner_contact_name: extractValue(order.partner_contact_name) || '',
      partner_contact_email: extractValue(order.partner_contact_email) || '',
      billing_street: extractValue(order.billing_street) || '',
      billing_city: extractValue(order.billing_city) || '',
      billing_state: extractValue(order.billing_state) || extractValue(order.billing_State) || '',
      billing_country: extractValue(order.billing_country) || '',
      billing_zip: extractValue(order.billing_zip) || '',
      status: extractValue(order.status) || 'active',
      payment_status: extractValue(order.payment_status) || 'pending',
      subscription_duration_years: extractValue(order.subscription_duration_years) || 1,
      has_addons: Boolean(extractValue(order.has_addons)),
      qty: extractValue(order.qty) || 1
    };
    
    setEditFormData(formData);
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setEditFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'number') {
      setEditFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : parseFloat(value)
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEditSubmit = async () => {
    if (!editingOrder) return;
    
    setEditLoading(true);
    setError('');
    try {
      const submitData = { ...editFormData };
      
      if (submitData.start_date) {
        submitData.start_date = `${submitData.start_date}T00:00:00.000Z`;
      }
      if (submitData.end_date) {
        submitData.end_date = `${submitData.end_date}T00:00:00.000Z`;
      }
      
      const response = await superAdminAPI.updateOrder(editingOrder._id, submitData);
      
      if (response.data.success) {
        setSuccessMessage('Order updated successfully');
        setShowEditModal(false);
        setRefreshTrigger(prev => prev + 1);
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        setError(response.data.error || 'Failed to update order');
      }
    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.error || 'Failed to update order');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClick = (order) => {
    setDeletingOrder(order);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingOrder) return;
    
    setDeleteLoading(true);
    setError('');
    try {
      const response = await superAdminAPI.deleteOrder(deletingOrder._id);
      if (response.data.success) {
        setSuccessMessage('Order deleted successfully');
        setShowDeleteModal(false);
        setRefreshTrigger(prev => prev + 1);
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        setError(response.data.error || 'Failed to delete order');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete order');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleReminderControlClick = (order, action) => {
    setSelectedReminderOrder(order);
    setReminderAction(action);
    setReminderReason('');
    setShowReminderControlModal(true);
  };

  const handleReminderControlConfirm = async () => {
    if (!selectedReminderOrder) return;
    
    setReminderControlLoading(true);
    setError('');
    
    try {
      const orderId = selectedReminderOrder._id;
      
      const response = await superAdminAPI.controlOrderReminder(
        orderId,
        {
          action: reminderAction,
          reason: reminderReason
        }
      );
      
      if (response.data.success) {
        setSuccessMessage(`Reminder emails ${reminderAction}d successfully`);
        setShowReminderControlModal(false);
        setRefreshTrigger(prev => prev + 1);
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        setError(response.data.error || `Failed to ${reminderAction} reminders`);
      }
    } catch (err) {
      console.error('Reminder control error:', err);
      setError(err.response?.data?.error || `Failed to ${reminderAction} reminders`);
    } finally {
      setReminderControlLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    
    const safeToString = (value) => {
      const extracted = extractValue(value);
      if (extracted === '') return '';
      return String(extracted);
    };

    if (statusFilter !== 'all') {
      const orderStatus = safeToString(order.status).toLowerCase();
      if (orderStatus !== statusFilter.toLowerCase()) {
        return false;
      }
    }

    if (productFilter !== 'all') {
      const orderProduct = safeToString(order.product_name);
      if (orderProduct !== productFilter) {
        return false;
      }
    }

    if (reminderStatusFilter !== 'all') {
      const orderReminderStatus = getReminderStatus(order);
      if (orderReminderStatus !== reminderStatusFilter) {
        return false;
      }
    }

    return (
      safeToString(order.account_name).toLowerCase().includes(searchLower) ||
      safeToString(order.reseller_name).toLowerCase().includes(searchLower) ||
      safeToString(order.invoice_owner).toLowerCase().includes(searchLower) ||
      safeToString(order.product_name).toLowerCase().includes(searchLower) ||
      safeToString(order.subscription_plan).toLowerCase().includes(searchLower) ||
      safeToString(order.product_owner_email).toLowerCase().includes(searchLower) ||
      safeToString(order.partner_contact_email).toLowerCase().includes(searchLower) ||
      safeToString(order.reseller_id).toLowerCase().includes(searchLower) ||
      safeToString(order.subscription_number).toString().includes(searchLower) ||
      safeToString(order.billing_city).toLowerCase().includes(searchLower)
    );
  });

  const uniqueProducts = ['all', ...new Set(
    orders
      .map(order => extractValue(order.product_name))
      .filter(value => value !== '')
      .map(String)
  )];
  
  const uniqueStatuses = ['all', ...new Set(
    orders
      .map(order => extractValue(order.status))
      .filter(value => value !== '')
      .map(String)
  )];

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (sortConfig.key) {
      let aValue = extractValue(a[sortConfig.key]);
      let bValue = extractValue(b[sortConfig.key]);
      
      if (aValue === '' && bValue === '') return 0;
      if (aValue === '') return 1;
      if (bValue === '') return -1;
      
      if (sortConfig.key === 'end_date' || sortConfig.key === 'start_date') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return sortConfig.direction === 'asc' 
            ? aDate - bDate 
            : bDate - aDate;
        }
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (aStr < bStr) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const showOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const handleRenewal = async (order) => {
    const safeOrder = {};
    Object.keys(order).forEach(key => {
      safeOrder[key] = extractValue(order[key]);
    });
    
    if (!safeOrder.quantity) {
      safeOrder.quantity = safeOrder.qty || 1;
    }
    
    const resellerId = extractValue(order.reseller_id) || extractValue(order.resellerId);
    
    let resellerInfo = null;
    if (resellerId) {
      resellerInfo = await fetchResellerInfo(resellerId);
    }
    
    if (!resellerInfo && safeOrder.reseller_name) {
      resellerInfo = {
        _id: resellerId,
        name: extractValue(safeOrder.reseller_name),
        email: extractValue(safeOrder.partner_contact_email),
        margin: 0,
        payment_mode: "Credit Card"
      };
    }
    
    if (!resellerInfo) {
      setError("Cannot process renewal: Reseller information not found");
      return;
    }
    
    // Calculate total amount for renewal
    const unitPrice = extractValue(safeOrder.retail_subscription_price) || 0;
    const quantity = safeOrder.quantity || 1;
    const totalAmount = unitPrice * quantity;
    
    setSelectedRenewalOrder({
      ...safeOrder,
      canHaveCartridges: canHaveCartridges(safeOrder),
      quantity: quantity,
      totalAmount: totalAmount,
      unitPrice: unitPrice,
      reseller_id: resellerId || resellerInfo._id
    });
    
    setReseller(resellerInfo);
    setShowRenewalModal(true);
  };

  const handleRenewalModalClose = () => {
    setShowRenewalModal(false);
    setSelectedRenewalOrder(null);
    setReseller(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const goBack = () => {
    navigate('/dashboard');
  };

  const totalRevenue = orders.reduce((sum, order) => {
    const price = extractValue(order.retail_subscription_price) || 0;
    const qty = extractValue(order.qty) || 1;
    return sum + (price * qty);
  }, 0);

  const activeOrders = orders.filter(order => {
    const status = extractValue(order.status);
    return status && status.toLowerCase() === 'active';
  }).length;
  
  const uniqueClients = new Set(
    orders
      .map(order => extractValue(order.account_name))
      .filter(value => value !== '')
      .map(String)
  ).size;
  
  const uniqueResellers = new Set(
    orders
      .map(order => extractValue(order.reseller_name))
      .filter(value => value !== '')
      .map(String)
  ).size;

  const shouldShowRenewalButton = (order) => {
    if (!order) return false;
    const status = extractValue(order.status);
    return status !== 'pending_payment' && status !== 'cancelled';
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <CButton 
            color="secondary" 
            variant="outline" 
            className="me-3"
            onClick={goBack}
          >
            ← Back to Dashboard
          </CButton>
          <div>
            <h2>
              <CIcon icon={cilList} className="me-2 text-primary" />
              All Orders
            </h2>
            <p className="text-medium-emphasis">
              Manage and view all subscription orders ({orders.length} total)
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <CButton color="primary" onClick={() => setRefreshTrigger(prev => prev + 1)}>
            <CIcon icon={cilReload} className="me-2" />
            Refresh
          </CButton>
        </div>
      </div>

      {error && (
        <CAlert color="danger" className="mb-3" dismissible onClose={() => setError('')}>
          {error}
        </CAlert>
      )}

      {successMessage && (
        <CAlert color="success" className="mb-3" dismissible onClose={() => setSuccessMessage('')}>
          {successMessage}
        </CAlert>
      )}

      <CRow className="mb-4">
        <CCol md={3}>
          <CCard className="text-center border-primary">
            <CCardBody>
              <h5>
                <CIcon icon={cilDollar} className="me-2" />
                Total Revenue
              </h5>
              <p className="fs-3 fw-bold text-primary">
                {formatCurrency(totalRevenue)}
              </p>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={3}>
          <CCard className="text-center border-success">
            <CCardBody>
              <h5>
                <CIcon icon={cilList} className="me-2" />
                Active Orders
              </h5>
              <p className="fs-3 fw-bold text-success">{activeOrders}</p>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={3}>
          <CCard className="text-center border-info">
            <CCardBody>
              <h5>
                <CIcon icon={cilUser} className="me-2" />
                Unique Clients
              </h5>
              <p className="fs-3 fw-bold text-info">{uniqueClients}</p>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={3}>
          <CCard className="text-center border-warning">
            <CCardBody>
              <h5>
                <CIcon icon={cilBuilding} className="me-2" />
                Unique Resellers
              </h5>
              <p className="fs-3 fw-bold text-warning">{uniqueResellers}</p>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard className="mb-4">
        <CCardBody>
          <CRow className="align-items-center mb-3">
            <CCol md={3}>
              <CInputGroup>
                <CFormInput
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <CButton color="primary" type="button">
                  <CIcon icon={cilMagnifyingGlass} />
                </CButton>
              </CInputGroup>
            </CCol>
            <CCol md={3}>
              <CFormSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                {uniqueStatuses.filter(s => s !== 'all').map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol md={3}>
              <CFormSelect
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
              >
                <option value="all">All Products</option>
                {uniqueProducts.filter(p => p !== 'all').map(product => (
                  <option key={product} value={product}>{product}</option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol md={3}>
              <CFormSelect
                value={reminderStatusFilter}
                onChange={(e) => setReminderStatusFilter(e.target.value)}
              >
                <option value="all">All Reminder Status</option>
                <option value="active">Reminders Active</option>
                <option value="paused">Reminders Paused</option>
              </CFormSelect>
            </CCol>
          </CRow>
          <div className="text-end">
            <span className="text-medium-emphasis me-3">
              Showing {currentOrders.length} of {filteredOrders.length} orders
            </span>
          </div>
        </CCardBody>
      </CCard>

      {loading ? (
        <div className="text-center py-5">
          <CSpinner color="primary" />
          <p className="mt-2">Loading orders...</p>
        </div>
      ) : (
        <>
          <CCard>
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <h5>Order Directory</h5>
              <CBadge color="primary" shape="rounded-pill">
                {sortedOrders.length} Orders
              </CBadge>
            </CCardHeader>
            <CCardBody>
              {currentOrders.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-medium-emphasis">No orders found</p>
                  {(searchTerm || statusFilter !== 'all' || productFilter !== 'all' || reminderStatusFilter !== 'all') && (
                    <CButton 
                      color="link" 
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                        setProductFilter('all');
                        setReminderStatusFilter('all');
                      }}
                    >
                      Clear all filters
                    </CButton>
                  )}
                </div>
              ) : (
                <>
                  <CTable striped hover responsive>
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('subscription_number')} style={{ cursor: 'pointer' }}>
                          Order # {sortConfig.key === 'subscription_number' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th onClick={() => handleSort('account_name')} style={{ cursor: 'pointer' }}>
                          Client {sortConfig.key === 'account_name' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th onClick={() => handleSort('reseller_name')} style={{ cursor: 'pointer' }}>
                          Reseller {sortConfig.key === 'reseller_name' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th>Product</th>
                        <th onClick={() => handleSort('end_date')} style={{ cursor: 'pointer' }}>
                          Dates {sortConfig.key === 'end_date' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th onClick={() => handleSort('retail_subscription_price')} style={{ cursor: 'pointer' }}>
                          Amount {sortConfig.key === 'retail_subscription_price' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th>Status</th>
                        <th>Reminders</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map(order => {
                        const totalInfo = formatTotalAmount(order);
                        const hasMultipleQuantity = totalInfo.quantity > 1;
                        
                        return (
                          <tr key={order._id}>
                            <td>
                              <strong>#{safeRender(order.subscription_number)}</strong>
                              <div className="small text-medium-emphasis">
                                ID: {order._id?.substring(0, 8)}...
                              </div>
                            </td>
                            <td>
                              <strong>{safeRender(order.account_name)}</strong>
                              {order.product_owner_email && (
                                <div className="small text-medium-emphasis">
                                  <CIcon icon={cilEnvelopeClosed} className="me-1" />
                                  {safeRender(order.product_owner_email)}
                                </div>
                              )}
                            </td>
                            <td>
                              <div>
                                {safeRender(order.reseller_name)}
                                {order.partner_contact_name && (
                                  <div className="small text-medium-emphasis">
                                    <CIcon icon={cilUser} className="me-1" />
                                    {safeRender(order.partner_contact_name)}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{safeRender(order.product_name)}</strong>
                                {order.subscription_plan && (
                                  <div className="small text-medium-emphasis">
                                    {safeRender(order.subscription_plan)}
                                  </div>
                                )}
                                {order.qty && order.qty > 1 && (
                                  <CBadge color="info" className="ms-1 mt-1">
                                    x{totalInfo.quantity}
                                  </CBadge>
                                )}
                                {canHaveCartridges(order) && (
                                  <CTooltip content="Can add cartridge storage">
                                    <CBadge color="warning" className="ms-1 mt-1">
                                      Cartridge
                                    </CBadge>
                                  </CTooltip>
                                )}
                              </div>
                            </td>
                            <td>
                              <div>
                                <div>
                                  <CIcon icon={cilCalendar} className="me-1" />
                                  <strong>Start:</strong> {formatDate(order.start_date)}
                                </div>
                                <div>
                                  <strong>End:</strong> {formatDate(order.end_date)}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="fw-bold">
                                {totalInfo.unitPrice}
                              </div>
                              {hasMultipleQuantity && (
                                <div className="small text-success">
                                  <strong>Total: {totalInfo.total}</strong>
                                  <div className="text-medium-emphasis">
                                    ({totalInfo.quantity} × {totalInfo.unitPrice})
                                  </div>
                                </div>
                              )}
                              {order.renewal_amount_customer && !hasMultipleQuantity && (
                                <div className="small text-medium-emphasis">
                                  Renewal: {formatCurrency(order.renewal_amount_customer, order.Currency)}
                                </div>
                              )}
                            </td>
                            <td>
                              <CBadge color={getStatusColor(order.status)}>
                                {safeRender(order.status)}
                              </CBadge>
                            </td>
                            <td>
                              <CBadge color={getReminderStatusColor(getReminderStatus(order))}>
                                {getReminderStatusText(getReminderStatus(order))}
                              </CBadge>
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                <CButton 
                                  color="info" 
                                  size="sm"
                                  onClick={() => showOrderDetails(order)}
                                >
                                  <CIcon icon={cilInfo} className="me-1" />
                                  Details
                                </CButton>
                                <CButton 
                                  color="warning" 
                                  size="sm"
                                  onClick={() => handleEditClick(order)}
                                  title="Edit Order"
                                >
                                  <CIcon icon={cilPencil} className="me-1" />
                                  Edit
                                </CButton>
                                <CButton 
                                  color="danger" 
                                  size="sm"
                                  onClick={() => handleDeleteClick(order)}
                                  title="Delete Order"
                                >
                                  <CIcon icon={cilTrash} className="me-1" />
                                  Delete
                                </CButton>
                                {shouldShowRenewalButton(order) && (
                                  <CButton 
                                    color="success" 
                                    size="sm"
                                    onClick={() => handleRenewal(order)}
                                    title="Renew Order"
                                  >
                                    <CIcon icon={cilSync} className="me-1" />
                                    Renew
                                  </CButton>
                                )}
                              </div>
                              <div className="mt-2">
                                {getReminderStatus(order) === 'active' ? (
                                  <CButton 
                                    color="warning" 
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleReminderControlClick(order, 'pause')}
                                    title="Pause reminder emails for this order"
                                  >
                                    <CIcon icon={cilBellExclamation} className="me-1" />
                                    Pause Reminders
                                  </CButton>
                                ) : (
                                  <CButton 
                                    color="success" 
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleReminderControlClick(order, 'resume')}
                                    title="Resume reminder emails for this order"
                                  >
                                    <CIcon icon={cilBell} className="me-1" />
                                    Resume Reminders
                                  </CButton>
                                )}
                              </div>
                               </td>
                            </tr>
                        );
                      })}
                    </tbody>
                  </CTable>

                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <CPagination>
                        <CPaginationItem 
                          aria-label="Previous" 
                          disabled={currentPage === 1}
                          onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                          style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        >
                          <CIcon icon={cilArrowLeft} />
                        </CPaginationItem>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNumber;
                          if (totalPages <= 5) {
                            pageNumber = i + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i;
                          } else {
                            pageNumber = currentPage - 2 + i;
                          }
                          
                          return (
                            <CPaginationItem
                              key={pageNumber}
                              active={currentPage === pageNumber}
                              onClick={() => paginate(pageNumber)}
                              style={{ cursor: 'pointer' }}
                            >
                              {pageNumber}
                            </CPaginationItem>
                          );
                        })}
                        
                        <CPaginationItem 
                          aria-label="Next" 
                          disabled={currentPage === totalPages}
                          onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                          style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                        >
                          <CIcon icon={cilArrowRight} />
                        </CPaginationItem>
                      </CPagination>
                    </div>
                  )}

                  <div className="text-center mt-3">
                    <p className="text-medium-emphasis">
                      Page {currentPage} of {totalPages} • Showing {currentOrders.length} orders per page
                    </p>
                  </div>
                </>
              )}
            </CCardBody>
          </CCard>
        </>
      )}

      {/* Detail Modal - Updated to show total amount */}
      <CModal 
        size="lg" 
        visible={showDetailModal} 
        onClose={() => setShowDetailModal(false)}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilInfo} className="me-2" />
            Order Details
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedOrder && (() => {
            const totalInfo = formatTotalAmount(selectedOrder);
            return (
              <CListGroup flush>
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Order Number:</strong>
                  <span>#{safeRender(selectedOrder.subscription_number)}</span>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Client Name:</strong>
                  <span>{safeRender(selectedOrder.account_name)}</span>
                </CListGroupItem>
                
                <CListGroupItem>
                  <strong>Contact Emails:</strong>
                  <div className="mt-1">
                    {selectedOrder.product_owner_email && (
                      <div>
                        <CIcon icon={cilEnvelopeClosed} className="me-2" />
                        Primary: {safeRender(selectedOrder.product_owner_email)}
                      </div>
                    )}
                    {selectedOrder.product_owner_email_2 && (
                      <div>
                        <CIcon icon={cilEnvelopeClosed} className="me-2" />
                        Secondary: {safeRender(selectedOrder.product_owner_email_2)}
                      </div>
                    )}
                  </div>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Reseller:</strong>
                  <span>{safeRender(selectedOrder.reseller_name)}</span>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Product:</strong>
                  <span>{safeRender(selectedOrder.product_name)}</span>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Subscription Plan:</strong>
                  <span>{safeRender(selectedOrder.subscription_plan)}</span>
                </CListGroupItem>
                
                <CListGroupItem>
                  <strong>Subscription Period:</strong>
                  <div className="mt-1">
                    <div>
                      <CIcon icon={cilCalendar} className="me-2" />
                      <strong>Start:</strong> {formatDate(selectedOrder.start_date)}
                    </div>
                    <div>
                      <strong>End:</strong> {formatDate(selectedOrder.end_date)}
                    </div>
                  </div>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Quantity:</strong>
                  <span className="fw-bold">{totalInfo.quantity}</span>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Unit Price:</strong>
                  <span>{totalInfo.unitPrice}</span>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Total Amount:</strong>
                  <span className="fw-bold text-success">{totalInfo.total}</span>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Renewal Amount:</strong>
                  <span>
                    {formatCurrency(selectedOrder.renewal_amount_customer, selectedOrder.Currency)}
                  </span>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Status:</strong>
                  <CBadge color={getStatusColor(selectedOrder.status)}>
                    {safeRender(selectedOrder.status)}
                  </CBadge>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Payment Status:</strong>
                  <CBadge color={selectedOrder.payment_status === 'paid' ? 'success' : 'warning'}>
                    {safeRender(selectedOrder.payment_status)}
                  </CBadge>
                </CListGroupItem>
                
                <CListGroupItem className="d-flex justify-content-between align-items-center">
                  <strong>Reminder Status:</strong>
                  <CBadge color={getReminderStatusColor(getReminderStatus(selectedOrder))}>
                    {getReminderStatusText(getReminderStatus(selectedOrder))}
                  </CBadge>
                </CListGroupItem>
                
                <CListGroupItem>
                  <strong>Order ID:</strong>
                  <div className="mt-1 small text-muted font-monospace">
                    {selectedOrder._id}
                  </div>
                </CListGroupItem>
              </CListGroup>
            );
          })()}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Edit Modal with quantity field */}
      <CModal 
        size="xl" 
        fullscreen="xl"
        visible={showEditModal} 
        onClose={() => setShowEditModal(false)}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilPencil} className="me-2" />
            Edit Order: #{editingOrder?.subscription_number}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {editingOrder && (
            <CForm>
              <h5 className="mb-3">Client Information</h5>
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Account Name *</CFormLabel>
                  <CFormInput
                    type="text"
                    name="account_name"
                    value={editFormData.account_name || ''}
                    onChange={handleEditChange}
                    placeholder="Client name"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Subscription Duration (Years)</CFormLabel>
                  <CFormInput
                    type="number"
                    name="subscription_duration_years"
                    value={editFormData.subscription_duration_years || 1}
                    onChange={handleEditChange}
                    min="1"
                  />
                </CCol>
              </CRow>

              <h5 className="mb-3 mt-4">Product & Subscription</h5>
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Subscription Plan</CFormLabel>
                  <CFormInput
                    type="text"
                    name="subscription_plan"
                    value={editFormData.subscription_plan || ''}
                    onChange={handleEditChange}
                    placeholder="Subscription plan"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Quantity</CFormLabel>
                  <CFormInput
                    type="number"
                    name="qty"
                    value={editFormData.qty || 1}
                    onChange={handleEditChange}
                    min="1"
                    step="1"
                    placeholder="Number of licenses"
                  />
                  <small className="text-muted">Number of subscriptions/licenses</small>
                </CCol>
              </CRow>

              <h5 className="mb-3 mt-4">Pricing Information</h5>
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Initial Amount (Customer)</CFormLabel>
                  <CFormInput
                    type="number"
                    name="initial_amount_customer"
                    value={editFormData.initial_amount_customer || ''}
                    onChange={handleEditChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Renewal Amount (Customer)</CFormLabel>
                  <CFormInput
                    type="number"
                    name="renewal_amount_customer"
                    value={editFormData.renewal_amount_customer || ''}
                    onChange={handleEditChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </CCol>
              </CRow>
              
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Initial Amount (Partner)</CFormLabel>
                  <CFormInput
                    type="number"
                    name="initial_amount_partner"
                    value={editFormData.initial_amount_partner || ''}
                    onChange={handleEditChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Renewal Amount (Partner)</CFormLabel>
                  <CFormInput
                    type="number"
                    name="renewal_amount_partner"
                    value={editFormData.renewal_amount_partner || ''}
                    onChange={handleEditChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </CCol>
              </CRow>
              
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Partner Discount Rate</CFormLabel>
                  <CFormInput
                    type="number"
                    name="partner_discount_rate"
                    value={editFormData.partner_discount_rate || ''}
                    onChange={handleEditChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Partner Margin Percentage</CFormLabel>
                  <CFormInput
                    type="number"
                    name="partner_margin_percentage"
                    value={editFormData.partner_margin_percentage || ''}
                    onChange={handleEditChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </CCol>
              </CRow>
              
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Reseller Margin</CFormLabel>
                  <CFormInput
                    type="number"
                    name="reseller_margin"
                    value={editFormData.reseller_margin || ''}
                    onChange={handleEditChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Payment Terms</CFormLabel>
                  <CFormSelect
                    name="payment_terms"
                    value={editFormData.payment_terms || 'immediate'}
                    onChange={handleEditChange}
                  >
                    <option value="immediate">Immediate</option>
                    <option value="net_30">Net 30</option>
                    <option value="net_60">Net 60</option>
                    <option value="net_90">Net 90</option>
                  </CFormSelect>
                </CCol>
              </CRow>

              <h5 className="mb-3 mt-4">Dates</h5>
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Start Date</CFormLabel>
                  <CFormInput
                    type="date"
                    name="start_date"
                    value={editFormData.start_date || ''}
                    onChange={handleEditChange}
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>End Date</CFormLabel>
                  <CFormInput
                    type="date"
                    name="end_date"
                    value={editFormData.end_date || ''}
                    onChange={handleEditChange}
                  />
                </CCol>
              </CRow>

              <h5 className="mb-3 mt-4">Contact Information</h5>
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Product Owner Name</CFormLabel>
                  <CFormInput
                    type="text"
                    name="product_owner_name"
                    value={editFormData.product_owner_name || ''}
                    onChange={handleEditChange}
                    placeholder="Product owner name"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Product Owner Email</CFormLabel>
                  <CFormInput
                    type="email"
                    name="product_owner_email"
                    value={editFormData.product_owner_email || ''}
                    onChange={handleEditChange}
                    placeholder="Product owner email"
                  />
                </CCol>
              </CRow>
              
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Partner Contact Name</CFormLabel>
                  <CFormInput
                    type="text"
                    name="partner_contact_name"
                    value={editFormData.partner_contact_name || ''}
                    onChange={handleEditChange}
                    placeholder="Partner contact name"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Partner Contact Email</CFormLabel>
                  <CFormInput
                    type="email"
                    name="partner_contact_email"
                    value={editFormData.partner_contact_email || ''}
                    onChange={handleEditChange}
                    placeholder="Partner contact email"
                  />
                </CCol>
              </CRow>

              <h5 className="mb-3 mt-4">Billing Information</h5>
              <CRow className="mb-3">
                <CCol md={12}>
                  <CFormLabel>Billing Street</CFormLabel>
                  <CFormInput
                    type="text"
                    name="billing_street"
                    value={editFormData.billing_street || ''}
                    onChange={handleEditChange}
                    placeholder="Street address"
                  />
                </CCol>
              </CRow>
              
              <CRow className="mb-3">
                <CCol md={4}>
                  <CFormLabel>Billing City</CFormLabel>
                  <CFormInput
                    type="text"
                    name="billing_city"
                    value={editFormData.billing_city || ''}
                    onChange={handleEditChange}
                    placeholder="City"
                  />
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Billing State</CFormLabel>
                  <CFormInput
                    type="text"
                    name="billing_state"
                    value={editFormData.billing_state || ''}
                    onChange={handleEditChange}
                    placeholder="State"
                  />
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Billing ZIP</CFormLabel>
                  <CFormInput
                    type="text"
                    name="billing_zip"
                    value={editFormData.billing_zip || ''}
                    onChange={handleEditChange}
                    placeholder="ZIP code"
                  />
                </CCol>
              </CRow>
              
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Billing Country</CFormLabel>
                  <CFormInput
                    type="text"
                    name="billing_country"
                    value={editFormData.billing_country || ''}
                    onChange={handleEditChange}
                    placeholder="Country code (e.g., US)"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Invoice Owner</CFormLabel>
                  <CFormInput
                    type="text"
                    name="invoice_owner"
                    value={editFormData.invoice_owner || ''}
                    onChange={handleEditChange}
                    placeholder="Invoice owner"
                  />
                </CCol>
              </CRow>

              <h5 className="mb-3 mt-4">Additional Information</h5>
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Distributor Reseller</CFormLabel>
                  <CFormInput
                    type="text"
                    name="distributor_reseller"
                    value={editFormData.distributor_reseller || ''}
                    onChange={handleEditChange}
                    placeholder="Distributor reseller"
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel className="d-block">Has Addons</CFormLabel>
                  <CFormCheck
                    type="checkbox"
                    name="has_addons"
                    checked={editFormData.has_addons || false}
                    onChange={handleEditChange}
                    label="This order has addons"
                  />
                </CCol>
              </CRow>

              <h5 className="mb-3 mt-4">Status</h5>
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel>Order Status</CFormLabel>
                  <CFormSelect
                    name="status"
                    value={editFormData.status || 'active'}
                    onChange={handleEditChange}
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="pending_payment">Pending Payment</option>
                    <option value="expired">Expired</option>
                    <option value="cancelled">Cancelled</option>
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Payment Status</CFormLabel>
                  <CFormSelect
                    name="payment_status"
                    value={editFormData.payment_status || 'pending'}
                    onChange={handleEditChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="refunded">Refunded</option>
                  </CFormSelect>
                </CCol>
              </CRow>
            </CForm>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setShowEditModal(false)} disabled={editLoading}>
            Cancel
          </CButton>
          <CButton color="primary" onClick={handleEditSubmit} disabled={editLoading}>
            {editLoading ? (
              <>
                <CSpinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Delete Confirmation Modal */}
      <CModal 
        visible={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilTrash} className="me-2 text-danger" />
            Delete Order
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {deletingOrder && (
            <>
              <p>Are you sure you want to delete this order?</p>
              
              <CListGroup flush>
                <CListGroupItem>
                  <strong>Order #:</strong> #{safeRender(deletingOrder.subscription_number)}
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Client:</strong> {safeRender(deletingOrder.account_name)}
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Product:</strong> {safeRender(deletingOrder.product_name)}
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Status:</strong> <CBadge color={getStatusColor(deletingOrder.status)}>
                    {safeRender(deletingOrder.status)}
                  </CBadge>
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Payment Status:</strong> <CBadge color={deletingOrder.payment_status === 'paid' ? 'success' : 'warning'}>
                    {safeRender(deletingOrder.payment_status)}
                  </CBadge>
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Reminder Status:</strong> <CBadge color={getReminderStatusColor(getReminderStatus(deletingOrder))}>
                    {getReminderStatusText(getReminderStatus(deletingOrder))}
                  </CBadge>
                </CListGroupItem>
              </CListGroup>
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
            Cancel
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <CSpinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Order'
            )}
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Reminder Control Modal */}
      <CModal 
        visible={showReminderControlModal} 
        onClose={() => setShowReminderControlModal(false)}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={reminderAction === 'pause' ? cilBellExclamation : cilBell} className="me-2" />
            {reminderAction === 'pause' ? 'Pause Reminders' : 'Resume Reminders'}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedReminderOrder && (
            <>
              <p>
                {reminderAction === 'pause' 
                  ? 'Are you sure you want to pause reminder emails for this order?' 
                  : 'Are you sure you want to resume reminder emails for this order?'}
              </p>
              
              <CListGroup flush>
                <CListGroupItem>
                  <strong>Order #:</strong> #{safeRender(selectedReminderOrder.subscription_number)}
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Client:</strong> {safeRender(selectedReminderOrder.account_name)}
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Product:</strong> {safeRender(selectedReminderOrder.product_name)}
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Current Status:</strong>{' '}
                  <CBadge color={getStatusColor(selectedReminderOrder.status)}>
                    {safeRender(selectedReminderOrder.status)}
                  </CBadge>
                </CListGroupItem>
                <CListGroupItem>
                  <strong>Current Reminder Status:</strong>{' '}
                  <CBadge color={getReminderStatusColor(getReminderStatus(selectedReminderOrder))}>
                    {getReminderStatusText(getReminderStatus(selectedReminderOrder))}
                  </CBadge>
                </CListGroupItem>
              </CListGroup>
              
              <div className="mt-3">
                <CFormLabel>
                  <strong>Reason (optional):</strong>
                  <small className="text-muted ms-1">
                    {reminderAction === 'pause' 
                      ? 'Why are you pausing reminders?' 
                      : 'Why are you resuming reminders?'}
                  </small>
                </CFormLabel>
                <CFormTextarea
                  value={reminderReason}
                  onChange={(e) => setReminderReason(e.target.value)}
                  placeholder={
                    reminderAction === 'pause' 
                      ? "e.g., Client requested pause, account on hold, special arrangement..." 
                      : "e.g., Client renewed, issue resolved, special period ended..."
                  }
                  rows={3}
                />
              </div>
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton 
            color="secondary" 
            onClick={() => setShowReminderControlModal(false)} 
            disabled={reminderControlLoading}
          >
            Cancel
          </CButton>
          <CButton 
            color={reminderAction === 'pause' ? "warning" : "success"} 
            onClick={handleReminderControlConfirm} 
            disabled={reminderControlLoading}
          >
            {reminderControlLoading ? (
              <>
                <CSpinner size="sm" className="me-2" />
                {reminderAction === 'pause' ? 'Pausing...' : 'Resuming...'}
              </>
            ) : (
              <>
                {reminderAction === 'pause' ? (
                  <>
                    <CIcon icon={cilBellExclamation} className="me-1" />
                    Pause Reminders
                  </>
                ) : (
                  <>
                    <CIcon icon={cilBell} className="me-1" />
                    Resume Reminders
                  </>
                )}
              </>
            )}
          </CButton>
        </CModalFooter>
      </CModal>

      {showRenewalModal && selectedRenewalOrder && (
        <QuickBooksRenewalModal
          show={showRenewalModal}
          onClose={handleRenewalModalClose}
          order={selectedRenewalOrder}
          products={products}
          reseller={reseller}
          onSecurePayment={() => {}}
          isSuperAdmin={true}
        />
      )}
    </div>
  );
};

export default SuperAdminOrders;